import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Stepper, useStepper } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Alert, Button } from '@gitbook/spine-react';

import { useNavigation } from '../../navigation';
import { GitSyncProviderName } from '../GitSyncProviders';
import type { GitSyncCurrentStatus } from './useGitSyncCurrentStatus';

const STEPS_RECORD: Record<GitSyncCurrentStatus['stage'], { index: number; title: string }> = {
    repo_fetch: {
        index: 0,
        title: 'Fetching your repository from remote provider',
    },
    config_check: {
        index: 1,
        title: 'Parsing your configuration file',
    },
    content_process: {
        index: 2,
        title: 'Processing pages and files',
    },
    finalize: {
        index: 3,
        title: 'Finalizing synchronization',
    },
};

const ALL_STEPS = Object.values(STEPS_RECORD).map((step) => ({ title: step.title }));

/**
 * Modal that displays the progress stages of the Git synchronization
 * through a vertical stepper.
 */
export function GitSyncProgressModalStepper(props: {
    space: api.Space;
    gitSyncStatus: GitSyncCurrentStatus | null;
}): React.ReactElement {
    const { space, gitSyncStatus } = props;
    const { navigate } = useNavigation();

    const stepsCount = ALL_STEPS.length + 1;

    const {
        completedSteps: stepperCompletedSteps,
        currentStepIndex: stepperCurrentStepIndex,
        onStepForward,
    } = useStepper({
        stepsCount,
    });

    React.useEffect(() => {
        if (gitSyncStatus?.stage) {
            const diff = STEPS_RECORD[gitSyncStatus?.stage].index - stepperCurrentStepIndex;
            if (diff > 0) {
                for (let i = 0; i < diff; i++) {
                    onStepForward();
                }
            }
        }
    }, [gitSyncStatus?.stage, onStepForward, stepperCurrentStepIndex]);

    const hasGitSyncStatus = Boolean(gitSyncStatus);
    const stateForStepper = React.useMemo(() => {
        if (gitSyncStatus?.state === 'success') {
            // All steps are completed
            return {
                completedSteps: new Set(Array.from({ length: stepsCount }, (_, i) => i)),
                currentStepIndex: stepsCount,
                erroredSteps: [],
            };
        }
        if (gitSyncStatus?.state === 'running') {
            // use state from stepper hook when sync is running
            return {
                completedSteps: stepperCompletedSteps,
                currentStepIndex: stepperCurrentStepIndex,
                erroredSteps: [],
            };
        }
        const currentStepIndex = gitSyncStatus?.stage
            ? STEPS_RECORD[gitSyncStatus?.stage].index
            : 0;
        const completedSteps = new Set(
            Array.from({ length: currentStepIndex }, (_, i) =>
                i < currentStepIndex ? i : null
            ).filter((step): step is number => step !== null)
        );
        return {
            completedSteps,
            currentStepIndex,
            erroredSteps: hasGitSyncStatus ? [currentStepIndex] : [],
        };
    }, [
        gitSyncStatus?.state,
        gitSyncStatus?.stage,
        hasGitSyncStatus,
        stepsCount,
        stepperCompletedSteps,
        stepperCurrentStepIndex,
    ]);

    const allStepsCompleted =
        gitSyncStatus?.state === 'success' || stateForStepper.completedSteps.size === stepsCount;

    return (
        <div className="flex flex-col items-center gap-6">
            {/* Vertical stepper */}
            <div className="flex flex-1 flex-col items-center">
                <Stepper
                    steps={ALL_STEPS}
                    completedSteps={stateForStepper.completedSteps}
                    currentStepIndex={stateForStepper.currentStepIndex}
                    erroredSteps={stateForStepper.erroredSteps}
                    styles="px-2"
                    orientation="vertical"
                    animate
                />
            </div>

            {gitSyncStatus?.state === 'timed-out' ? (
                <Alert
                    className="flex"
                    kind="danger"
                    details="The latest synchronization timed-out. Please try again or contact support for assistance."
                />
            ) : null}

            {gitSyncStatus?.state === 'failure' && gitSyncStatus?.error ? (
                <Alert kind="danger" details={gitSyncStatus?.error} />
            ) : null}

            {gitSyncStatus?.state === 'success' && allStepsCompleted ? (
                <Alert
                    className="flex"
                    kind="success"
                    details={`Your space content is synchronized with  ${
                        space.gitSync?.installationProvider
                            ? GitSyncProviderName[space.gitSync.installationProvider]
                            : 'Git'
                    }`}
                />
            ) : null}

            {/* Footer */}
            {gitSyncStatus?.state === 'failure' ? (
                <div className="flex items-center justify-center gap-4 border-t p-6">
                    <div className="flex w-full max-w-[250px] justify-center self-center">
                        <Button
                            label="Configuration settings"
                            kind="secondary"
                            size="large"
                            leadingIcon={Icon.Settings}
                            onPress={() => {
                                navigate({
                                    screen: {
                                        type: 'space',
                                        organization: space.organization,
                                        space: space.id,
                                        sideSheet: {
                                            type: 'integrations',
                                            view: {
                                                type: 'integration',
                                                integration: space.gitSync?.integration,
                                            },
                                        },
                                    },
                                });
                            }}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
}
